/** @jsx jsx */
import '@fortawesome/fontawesome-svg-core/styles.css';

import { Global } from '@emotion/core';
import { config, library } from '@fortawesome/fontawesome-svg-core';
import { fab } from '@fortawesome/free-brands-svg-icons';
import { faAt, faEnvelope, faHeart, faPhone, faRss } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { MDXProvider } from '@mdx-js/react';
import {
  Avatar,
  Box,
  Embed,
  Flex,
  Link as LinkThemeUi,
} from '@theme-ui/components';
import { Link as LinkGatsby } from 'gatsby';
import { useCallback, useEffect, useRef, useState } from 'react';
import { jsx, Layout } from 'theme-ui';

import FeedContext from 'gatsby-theme-chronoblog/src/contexts/context-feed';
import AuthorBanner, {
  AuthorBannerAvatar,
  AuthorBannerDescription,
  AuthorBannerHeading,
} from 'gatsby-theme-chronoblog/src/components/author-banner';
import Button from 'gatsby-theme-chronoblog/src/components/button';
import FeedItems from 'gatsby-theme-chronoblog/src/components/feed-items';
import FeedSearch from 'gatsby-theme-chronoblog/src/components/feed-search';
import LightDarkSwitchButton from 'gatsby-theme-chronoblog/src/components/light-dark-switch-button';
import SEO from 'gatsby-theme-chronoblog/src/components/seo';
import SocialLinks from 'gatsby-theme-chronoblog/src/components/social-links';
import Tags from 'gatsby-theme-chronoblog/src/components/tags';
import { SubscriptionForm } from './subscription-form';

config.autoAddCss = false;
library.add(fab, faEnvelope, faHeart, faPhone, faAt, faRss);

const Link = ({ to, children, ...props }) => (
  <LinkGatsby to={to} {...props}>
    <LinkThemeUi>{children}</LinkThemeUi>
  </LinkGatsby>
);
const A = ({ children, ...props }) => <a {...props}>{children}</a>;
const Div = ({ children, ...props }) => <div {...props}>{children}</div>;
const loadMermaidScript = () => {
  if (typeof window === 'undefined') return Promise.resolve(null);
  if (window.mermaid) return Promise.resolve(window.mermaid);
  if (window.__mermaidLoadPromise) return window.__mermaidLoadPromise;

  window.__mermaidLoadPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/mermaid@10/dist/mermaid.min.js';
    script.async = true;
    script.onload = () => resolve(window.mermaid);
    script.onerror = () => reject(new Error('Failed to load Mermaid script'));
    document.head.appendChild(script);
  });

  return window.__mermaidLoadPromise;
};

const Pre = ({ children, ...props }) => {
  const childProps = children && children.props ? children.props : {};
  const codeClassName = childProps.className || '';
  const isMermaid = codeClassName.includes('language-mermaid');
  const rawCode = childProps.children;
  const mermaidCode = Array.isArray(rawCode) ? rawCode.join('') : rawCode;
  const containerRef = useRef(null);

  useEffect(() => {
    if (!isMermaid || !mermaidCode || !containerRef.current) return;

    let isMounted = true;

    const renderMermaid = async () => {
      const mermaid = await loadMermaidScript();
      if (!mermaid) return;

      mermaid.initialize({
        startOnLoad: false,
      });

      const id = `mermaid-${Math.random().toString(36).slice(2)}`;
      const { svg } = await mermaid.render(id, mermaidCode);

      if (isMounted && containerRef.current) {
        containerRef.current.innerHTML = svg;
      }
    };

    renderMermaid().catch((error) => {
      console.error('Failed to render mermaid diagram', error);
    });

    return () => {
      isMounted = false;
    };
  }, [isMermaid, mermaidCode]);

  if (!isMermaid) {
    return <pre {...props}>{children}</pre>;
  }

  return <div ref={containerRef} />;
};

const authorBannerComponents = {
  AuthorBanner,
  AuthorBannerHeading,
  AuthorBannerDescription,
  AuthorBannerAvatar,
};

const themeUiComponents = {
  Avatar,
  Box,
  Embed,
  Flex,
};

const components = {
  SEO,
  Tags,
  FeedItems,
  FeedSearch,
  SocialLinks,
  LightDarkSwitchButton,
  Button,
  Link,
  A,
  Div,
  Pre,
  pre: Pre,
  FontAwesomeIcon,
  ...themeUiComponents,
  ...authorBannerComponents,
  SubscriptionForm,
};

/**
 * site layout
 *
 * @param {*} props spread props
 */
export default ({ children, ...props }) => {
  //
  const [searchInput, setSearchInput] = useState('');

  const onChangeSearchInput = useCallback((event) => {
    setSearchInput(event.currentTarget.value);
  }, []);
  //
  return (
    <Layout {...props}>
      <Global
        styles={{
          '*': {
            boxSizing: 'border-box',
          },
          body: {
            margin: 0,
            overflowWrap: 'break-word',
            wordWrap: 'break-word',
          },
        }}
      />
      <SEO />
      <MDXProvider components={components}>
        <FeedContext.Provider value={{ searchInput, onChangeSearchInput }}>
          {children}
        </FeedContext.Provider>
      </MDXProvider>
    </Layout>
  );
};
